import { useEffect, useRef, type ReactNode } from 'react';
import type { HelpFaqCallout } from '../lib/types';
import { AnswerCard, type AnswerCta, type AnswerSource } from './AnswerCard';
import { TypingIndicator } from './TypingIndicator';
import { UserBubble } from './UserBubble';

/** A HelpMessage already shaped for display; parsing `answer_md` happens upstream. */
export type DisplayMessage =
  | { id: string; role: 'user'; text: string }
  | {
      id: string;
      role: 'assistant';
      paragraphs: ReactNode[];
      callout?: HelpFaqCallout | null;
      cta?: AnswerCta | null;
      source?: AnswerSource | null;
      notice?: string | null;
      variant?: 'answer' | 'fallback';
      extras?: ReactNode;
    };

export interface ChatThreadProps {
  messages: DisplayMessage[];
  /** Renders the typing indicator at the bottom. */
  pending?: boolean;
  emptyState?: ReactNode;
}

/** The right pane's scroll region. Decides nothing about content; the shell paints the ground. */
export function ChatThread({ messages, pending = false, emptyState = null }: ChatThreadProps) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [messages.length, pending]);

  if (messages.length === 0) {
    return <div className="min-h-0 flex-1 overflow-y-auto">{emptyState}</div>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4 xl:px-5 xl:py-[18px]">
      {messages.map((message) =>
        message.role === 'user' ? (
          <UserBubble key={message.id} text={message.text} />
        ) : (
          <AnswerCard
            key={message.id}
            paragraphs={message.paragraphs}
            callout={message.callout}
            cta={message.cta}
            source={message.source}
            notice={message.notice}
            variant={message.variant}
          >
            {message.extras}
          </AnswerCard>
        ),
      )}
      {pending && <TypingIndicator />}
      <div ref={endRef} />
    </div>
  );
}
